import React, { useState, useEffect, useRef } from "react";
import {
  EventsPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
  ButtonWrapper,
  TitleWrapper,
  EditAndDeleteContainer,
} from "./EventsPage.styles";
import Sidebar from "../../components/Sidebar";
import Table, { TableText } from "../../components/Table";
import TableMetrics from "../../components/TableMetrics";
import Button from "../../components/Buttons/Button";
import IconButton from "../../components/Buttons/IconButton";
import { CreateModal, ConfirmModal } from "../../components/Modals";
import DeletedRow from "../../components/DeletedRow";
import RestoredRow from "../../components/RestoredRow";

import {
  APIResponse,
  Event,
} from "../../services/eventService/eventService.types";
import {
  fetchEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from "../../services/eventService/eventService";

import { Course } from "../../services/courseService/courseService.types";
import { fetchAllCourses } from "../../services/courseService/courseService";
import { useSubscription } from "../../hooks/useSubscription";
import { formatServerErrors } from "../../utils/errorHandler";

import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { useListPage } from "hooks/useListPage";
import { getServerErrorData } from "utils/httpError";
import { delIcon, editIcon } from "assets/icons";

const EventsPage: React.FC = () => {
  const {
    items: events,
    setItems: setEvents,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
    reload: loadEvents,
  } = useListPage<Event, APIResponse<Event>>({
    fetcher: ({ page, pageSize: size, orderBy }) => fetchEvents(page, size, orderBy),
    select: (response) => ({
      items: response.results.map((evt) => ({
        ...evt,
        course_title: evt.course_title,
        course: evt.course,
      })),
      total: response.total_events,
    }),
    onLoaded: (response) => setEventCount(response.total_events),
    errorMessage: "Ошибка загрузки событий",
    initialPageSize: 5,
  });
  const [courses, setCourses] = useState<Course[]>([]);
  const [eventCount, setEventCount] = useState<number>(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [externalErrors, setExternalErrors] = useState<Record<string, string>>({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [pendingDelete, setPendingDelete] = useState<number | null>(null);
  const [recentlyCreated, setRecentlyCreated] = useState<number | null>(null);
  const [recentlyEdited, setRecentlyEdited] = useState<number | null>(null);
  const [recentlyRestored, setRecentlyRestored] = useState<number | null>(null);
  const deleteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const toast = useNotify();
  const { hasActiveSubscription } = useSubscription();

  useEffect(() => {
    const loadCourses = async () => {
      try {
        const list = await fetchAllCourses();
        setCourses(list);
      } catch (err) {
        logger.error("Ошибка при загрузке курсов:", err);
      }
    };
    loadCourses();
  }, []);

  const handleCreateEvent = async (
    formData: Record<string, string | File[] | null>
  ) => {
    setExternalErrors({});
    if (!formData.course) {
      toast.show({
        severity: "warn",
        summary: "Внимание",
        detail: "Выберите курс",
        life: 3000,
      });
      return;
    }
    const courseId = Number(formData.course as string);
    if (isNaN(courseId)) {
      toast.show({
        severity: "error",
        summary: "Ошибка",
        detail: "Неверный ID курса",
        life: 3000,
      });
      return;
    }
    const payload = {
      course: courseId,
      calendar_day: formData.calendar_day as string,
      description: formData.description as string,
      time: formData.time as string,
    };
    try {
      const newEvent = await createEvent(payload as any);
      toast.show({
        severity: "success",
        summary: "Успех",
        detail: "Событие добавлено",
        life: 3000,
      });
      closeCreateModal();
      setEvents((prev) => [newEvent, ...prev]);
      setEventCount((prev) => prev + 1);
      setRecentlyCreated(newEvent.id);
      setTimeout(() => setRecentlyCreated(null), 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        if (formattedErrors.error || formattedErrors.non_field_errors) {
          toast.show({
            severity: "error",
            summary: "Ошибка",
            detail: formattedErrors.error || formattedErrors.non_field_errors || "Не удалось создать событие",
            life: 3000,
          });
        }
      } else {
        logger.error("Ошибка при создании события:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось создать событие",
          life: 3000,
        });
      }
    }
  };

  const handleEditEvent = async (
    formData: Record<string, string | File[] | null>
  ) => {
    if (!editingEvent) return;
    setExternalErrors({});
    if (!formData.course) {
      toast.show({
        severity: "warn",
        summary: "Внимание",
        detail: "Выберите курс",
        life: 3000,
      });
      return;
    }
    const courseId = Number(formData.course as string);
    const payload = {
      course: courseId,
      calendar_day: formData.calendar_day as string,
      description: formData.description as string,
      time: formData.time as string,
    };
    logger.log(payload);
    try {
      const updated = await updateEvent(editingEvent.id, payload as any);
      toast.show({
        severity: "success",
        summary: "Успех",
        detail: "Событие обновлено",
        life: 3000,
      });
      setRecentlyEdited(editingEvent.id);
      setEvents((prev) =>
        prev.map((ev) => (ev.id === updated.id ? updated : ev))
      );
      closeEditModal();
      setTimeout(() => setRecentlyEdited(null), 3000);
    } catch (error) {
      const serverErrors = getServerErrorData(error);
      if (serverErrors) {
        const formattedErrors = formatServerErrors(serverErrors);
        setExternalErrors(formattedErrors);
        if (formattedErrors.error || formattedErrors.non_field_errors) {
          toast.show({
            severity: "error",
            summary: "Ошибка",
            detail: formattedErrors.error || formattedErrors.non_field_errors || "Не удалось обновить событие",
            life: 3000,
          });
        }
      } else {
        logger.error("Ошибка при обновлении события:", error);
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось обновить событие",
          life: 3000,
        });
      }
    }
  };

  const openEditModal = (event: Event) => {
    setEditingEvent(event);
    setIsEditModalOpen(true);
  };
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEvent(null);
  };
  const openCreateModal = () => setIsCreateModalOpen(true);
  const closeCreateModal = () => setIsCreateModalOpen(false);

  const openDeleteModal = (event: Event) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setEventToDelete(null);
  };
  const confirmDelete = () => {
    if (eventToDelete) {
      handleDelete(eventToDelete.id);
      closeDeleteModal();
    }
  };

  const handleDelete = (id: number) => {
    setPendingDelete(id);
    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        await deleteEvent(id);
        setPendingDelete(null);
        toast.show({
          severity: "success",
          summary: "Успех",
          detail: "Событие удалено",
          life: 2000,
        });
        setEvents((prev) => prev.filter((ev) => ev.id !== id));
        setEventCount((prev) => prev - 1);
        loadEvents();
      } catch {
        toast.show({
          severity: "error",
          summary: "Ошибка",
          detail: "Не удалось удалить событие",
          life: 3000,
        });
        setPendingDelete(null);
      }
    }, 5000);
  };

  const restoreEventHandler = () => {
    if (deleteTimeoutRef.current) {
      clearTimeout(deleteTimeoutRef.current);
      deleteTimeoutRef.current = null;
    }
    const restoredId = pendingDelete;
    setPendingDelete(null);
    setRecentlyRestored(restoredId);
    toast.show({
      severity: "success",
      summary: "Восстановлено",
      detail: "Удаление события отменено",
      life: 3000,
    });
    setTimeout(() => setRecentlyRestored(null), 3000);
  };

  const courseOptions = courses.map((c) => ({
    label: c.title,
    value: String(c.id),
  }));

  const headers = [
    { label: "Курс", key: "course", fieldName: "course__title", width: "50%" },
    { label: "Событие", key: "description", fieldName: "description" },
    { label: "Дата", key: "calendar_day", fieldName: "calendar_day__date" },
    { label: "Время", key: "time", fieldName: "time", sortable: false },
    {
      label: "",
      key: "editAndDelete",
      sortable: false,
      fieldName: "editAndDelete",
    },
  ];

  let rows = events.map((event) => {
    if (event.id === pendingDelete) {
      return {
        description: (
          <DeletedRow
            id={event.id}
            onRestore={restoreEventHandler}
            colSpan={headers.length}
            message={`Событие "${event.description}" будет удалено через 5 секунд.`}
            targetText="Отменить удаление"
          />
        ),
      };
    }
    return {
      course: <TableText>{event.course_title}</TableText>,
      description: <TableText>{event.description}</TableText>,
      calendar_day: event.calendar_day,
      time: event.time,
      editAndDelete: hasActiveSubscription ? (
        <EditAndDeleteContainer>
          <IconButton
            icon={delIcon}
            tone="danger"
            altText="Удалить"
            onClick={() => openDeleteModal(event)}
          />
          <IconButton
            icon={editIcon}
            altText="Редактировать"
            onClick={() => openEditModal(event)}
          />
        </EditAndDeleteContainer>
      ) : null,
    };
  });

  if (recentlyRestored !== null) {
    const idx = events.findIndex((ev) => ev.id === recentlyRestored);
    if (idx !== -1) {
      rows.splice(idx + 1, 0, {
        description: <RestoredRow colSpan={headers.length} message="Событие успешно восстановлено" />,
      });
    }
  }
  if (recentlyCreated !== null) {
    const idx = events.findIndex((ev) => ev.id === recentlyCreated);
    if (idx !== -1) {
      rows.splice(idx + 1, 0, {
        description: <RestoredRow colSpan={headers.length} message="Событие успешно создано" />,
      });
    }
  }
  if (recentlyEdited !== null) {
    const idx = events.findIndex((ev) => ev.id === recentlyEdited);
    if (idx !== -1) {
      rows.splice(idx + 1, 0, {
        description: <RestoredRow colSpan={headers.length} message="Событие успешно отредактировано" />,
      });
    }
  }

  return (
    <EventsPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <TitleWrapper>
            <Title>Календарь</Title>
            {hasActiveSubscription && (
              <ButtonWrapper>
                <Button
                  onClick={openCreateModal}
                  type="button"
                  variant="primary"
                  size="lg"
                >
                  Добавить событие
                </Button>
              </ButtonWrapper>
            )}
          </TitleWrapper>
          <TableMetrics
            items={[
              { label: "Всего событий", value: eventCount },
            ]}
          />
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Событий пока нет",
            description: "Запланированные занятия и встречи появятся в этом списке.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          rowAmountOptions={[
            { label: "5 событий", value: 5 },
            { label: "15 событий", value: 15 },
            { label: "20 событий", value: 20 },
          ]}
          minHeight="230px"
          loading={loading}
          specialRowKey="description"
          onSort={handleSort}
          remHeight={234}
        />
      </ContentWrapper>

      {isCreateModalOpen && (
        <CreateModal
          title="Добавление события"
          fields={[
            { name: "course", type: "selectWithInput", placeholder: "Курс", options: courseOptions, required: true },
            { name: "calendar_day", type: "date", placeholder: "Дата", required: true },
            { name: "description", type: "textarea", placeholder: "Описание", required: true },
            { name: "time", type: "time", placeholder: "Время", required: true },
          ]}
          onSubmit={handleCreateEvent}
          submitButtonText="Сохранить"
          onCancel={closeCreateModal}
          hasDragAndDrop={false}
          initialValues={{ time: "00:00" }}
          externalErrors={externalErrors}
        />
      )}

      {isEditModalOpen && editingEvent && (
        <CreateModal
          title="Редактирование события"
          fields={[
            { name: "course", type: "selectWithInput", placeholder: "Курс", options: courseOptions, required: true },
            { name: "calendar_day", type: "date", placeholder: "Дата", required: true },
            { name: "description", type: "textarea", placeholder: "Описание", required: true },
            { name: "time", type: "time", placeholder: "Время", required: true },
          ]}
          onSubmit={handleEditEvent}
          submitButtonText="Сохранить"
          onCancel={closeEditModal}
          initialValues={{
            course: String(editingEvent.course),
            calendar_day: editingEvent.calendar_day,
            description: editingEvent.description,
            time: editingEvent.time,
          }}
          hasDragAndDrop={false}
          externalErrors={externalErrors}
        />
      )}

      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Подтверждение удаления"
          message={`Вы уверены, что хотите удалить событие "${eventToDelete?.description}"?`}
          onConfirm={confirmDelete}
          onCancel={closeDeleteModal}
          confirmButtonText="Удалить"
        />
      )}
    </EventsPageContainer>
  );
};

export default EventsPage;
